import { useState } from 'react';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';
import { X, Send } from 'lucide-react';
import api from '../utils/api';

// WhatsApp glyph (lucide has none)
export function WhatsAppIcon({ className = 'w-4 h-4' }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.25-.46-2.38-1.47-.88-.79-1.47-1.76-1.65-2.06-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.21 3.08c.15.2 2.1 3.2 5.08 4.49.71.31 1.26.49 1.69.63.71.23 1.36.2 1.87.12.57-.08 1.76-.72 2.01-1.42.25-.7.25-1.29.17-1.42-.07-.12-.27-.2-.57-.35zM12.05 21.5h-.01a9.4 9.4 0 0 1-4.8-1.31l-.34-.2-3.57.94.95-3.48-.22-.36a9.43 9.43 0 0 1-1.45-5.03c0-5.2 4.24-9.44 9.45-9.44a9.4 9.4 0 0 1 6.68 2.77 9.38 9.38 0 0 1 2.76 6.68c0 5.21-4.24 9.44-9.45 9.44zm8.04-17.48A11.3 11.3 0 0 0 12.05.7C5.78.7.68 5.8.68 12.06c0 2 .52 3.96 1.52 5.68L.58 23.3l5.7-1.5a11.33 11.33 0 0 0 5.77 1.47h.01c6.26 0 11.36-5.1 11.37-11.36 0-3.04-1.18-5.89-3.34-8.04z" />
    </svg>
  );
}

// "Send via WhatsApp" button + dialog, shared by every printable document.
//   documentType  SALE_CHALLAN | SALE_INVOICE | MANUFACTURING_REPORT | QUOTATION |
//                 QUALITY_REPORT | CFT_SHEET | DELIVERY_CHALLAN_BOXES
//   referenceId   document number/id shown in the caption and file name
//   getHtml       async () => the same full HTML the page prints (rendered to PDF on the server)
//   fileName      optional PDF name, e.g. "Quotation QT-1042.pdf"
//   defaultPhone  optional pre-filled number (e.g. the client's)
export default function WhatsAppSendButton({
  documentType, referenceId, getHtml, fileName, defaultPhone = '', label = 'WhatsApp', className, disabled,
}) {
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState('');
  const [caption, setCaption] = useState('');
  const [sending, setSending] = useState(false);

  const openDialog = () => {
    setPhone(defaultPhone || '');
    setCaption('');
    setOpen(true);
  };

  const close = () => { if (!sending) setOpen(false); };

  const send = async (e) => {
    e.preventDefault();
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 10) return toast.error('Enter a valid WhatsApp number');
    setSending(true);
    const toastId = toast.loading('Preparing PDF and sending…');
    try {
      const html = await getHtml();
      if (!html) throw new Error('Nothing to send');
      const fd = new FormData();
      fd.append('phone', phone);
      fd.append('documentType', documentType);
      fd.append('referenceId', referenceId ?? '');
      if (caption.trim()) fd.append('customCaption', caption.trim());
      if (fileName) fd.append('fileName', fileName);
      fd.append('baseUrl', window.location.origin);
      fd.append('html', new Blob([html], { type: 'text/html' }), 'document.html');
      const r = await api.post('/whatsapp/send-document', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000, // PDF rendering + upload can take a while
      });
      toast.success(r.data.message || 'Sent on WhatsApp', { id: toastId });
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Could not send', { id: toastId });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={openDialog}
        disabled={disabled}
        title="Send via WhatsApp"
        className={className || 'flex items-center gap-2 px-3 py-2 rounded-lg border border-green-200 bg-green-50 hover:bg-green-100 text-sm font-semibold text-green-700 disabled:opacity-50'}
      >
        <WhatsAppIcon /> {label}
      </button>

      {open && createPortal(
        <div className="fixed inset-0 z-[9500] flex items-center justify-center p-4 bg-black/40" onClick={(e) => e.target === e.currentTarget && close()}>
          <form onSubmit={send} className="w-full max-w-sm bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100">
              <div className="w-9 h-9 rounded-xl bg-green-100 text-green-600 flex items-center justify-center"><WhatsAppIcon className="w-5 h-5" /></div>
              <div className="min-w-0 flex-1">
                <h2 className="font-bold text-gray-900 text-sm">Send via WhatsApp</h2>
                <p className="text-xs text-gray-500 truncate">{fileName || 'PDF document'}</p>
              </div>
              <button type="button" onClick={close} className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100" aria-label="Close"><X className="w-4 h-4" /></button>
            </div>

            <div className="px-5 py-4 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp number</label>
                <input
                  autoFocus
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="e.g. 98765 43210"
                  inputMode="tel"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                />
                <p className="text-xs text-gray-400 mt-1">10-digit Indian numbers get +91 added automatically</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message <span className="text-gray-400 font-normal">(optional)</span></label>
                <textarea
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  rows={3}
                  maxLength={1000}
                  placeholder="Leave empty to send the document name and company name"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 px-5 py-3 bg-gray-50 border-t border-gray-100">
              <button type="button" onClick={close} disabled={sending} className="px-4 py-2 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-100">Cancel</button>
              <button type="submit" disabled={sending} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-green-600 hover:bg-green-700 text-white text-sm font-semibold disabled:opacity-60">
                <Send className="w-4 h-4" /> {sending ? 'Sending…' : 'Send'}
              </button>
            </div>
          </form>
        </div>,
        document.body,
      )}
    </>
  );
}
